import { create } from 'zustand';
import { Service } from '@/types';
import { supabase } from '@/lib/supabase';

interface ServicesState {
  services: Service[];
  isLoading: boolean;
  error: string | null;
  fetchServices: () => Promise<void>;
  addService: (service: Omit<Service, 'id'>) => Promise<void>;
  updateService: (service: Service) => Promise<void>;
  removeService: (id: string) => Promise<void>;
  getServices: () => Service[];
}

function loadServicesCache() {
  try {
    const cache = localStorage.getItem('services-cache');
    if (cache) return JSON.parse(cache);
  } catch {}
  return [];
}
function saveServicesCache(services: Service[]) {
  try {
    localStorage.setItem('services-cache', JSON.stringify(services));
  } catch {}
}

export const useServicesStore = create<ServicesState>()((set, get) => ({
  services: loadServicesCache(),
  isLoading: false,
  error: null,

  fetchServices: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('name');

      if (error) throw error;
      set({ services: data || [], isLoading: false });
      saveServicesCache(data || []);
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  addService: async (service) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('services')
        .insert([service])
        .select()
        .single();

      if (error) throw error;
      set((state) => {
        const updated = [...state.services, data];
        saveServicesCache(updated);
        return { services: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  updateService: async (updatedService) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('services')
        .update(updatedService)
        .eq('id', updatedService.id);

      if (error) throw error;
      set((state) => {
        const updated = state.services.map((service) =>
          service.id === updatedService.id ? updatedService : service
        );
        saveServicesCache(updated);
        return { services: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  removeService: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);

      if (error) throw error;
      set((state) => {
        const updated = state.services.filter((service) => service.id !== id);
        saveServicesCache(updated);
        return { services: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  getServices: () => get().services,
}));
